/**
 * Copy / Download controls for the cofactor.md view, rendered in the canvas
 * bottom-right (in line with the version dropdown). Both emit the full-fidelity,
 * re-compilable `.cofactor.md`.
 *
 * @module FlowDesigner/spec/SpecActions
 */

import React from 'react';
import { Button, useToast } from '@chatsdet/ui';
import { CopySimple, Check, DownloadSimple } from '@phosphor-icons/react';

export const SpecActions: React.FC<{ fullSource: string; fileName: string }> = ({
  fullSource,
  fileName,
}) => {
  const { toast } = useToast();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullSource);
      setCopied(true);
      toast.success('Copied cofactor.md');
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDownload = () => {
    try {
      const blob = new Blob([fullSource], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Downloaded cofactor.md');
    } catch {
      toast.error('Failed to download');
    }
  };

  return (
    <div className="flex items-center gap-0.5 rounded-lg border border-border/60 bg-background/80 px-1 py-0.5 shadow-sm backdrop-blur">
      <Button variant="icon" size="sm" iconOnly tooltip="Copy cofactor.md" onPress={handleCopy}>
        {copied ? <Check size={16} /> : <CopySimple size={16} />}
      </Button>
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tooltip="Download .cofactor.md"
        onPress={handleDownload}
      >
        <DownloadSimple size={16} />
      </Button>
    </div>
  );
};
